import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Toolbar, CircularProgress } from '@mui/material';
import { AdminProvider, useAdmin } from './context/AdminContext';
import { getDefaultRoute } from './constants/rbac';

import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Drivers from './pages/Drivers';
import DriverDetail from './pages/DriverDetail';
import DriverSchedule from './pages/DriverSchedule';
import StaffSchedule from './pages/StaffSchedule';
import MySchedule from './pages/MySchedule';
import Warehouse from './pages/Warehouse';
import Tasks from './pages/Tasks';
import InspectionsList from './pages/InspectionsList';
import PushNotifications from './pages/PushNotifications';
import ChatSupport from './components/ChatSupport';
import CreateUser from './pages/CreateUser';
import AccessDenied from './pages/AccessDenied';

import './App.css';

const theme = createTheme({
  palette: {
    primary: { main: '#000000' },
    secondary: { main: '#ffffff' },
    background: { default: '#ffffff', paper: '#ffffff' },
    text: { primary: '#000000', secondary: '#666666' },
    button: { main: '#DD2424' },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          backgroundColor: '#DD2424',
          color: '#ffffff',
          '&:hover': { backgroundColor: '#b81d1d' },
        },
        outlined: {
          borderColor: '#DD2424',
          color: '#DD2424',
          '&:hover': {
            borderColor: '#b81d1d',
            backgroundColor: 'rgba(221, 36, 36, 0.04)',
          },
        },
      },
    },
  },
});

const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

const Layout = ({ children }) => (
  <Box sx={{ display: 'flex' }}>
    <Navbar />
    <Sidebar />
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 3,
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
      }}
    >
      <Toolbar />
      {children}
    </Box>
  </Box>
);

const ProtectedRoute = ({ children, module }) => {
  const { user, loading, hasAccess } = useAdmin();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{module && !hasAccess(module) ? <AccessDenied module={module} /> : children}</Layout>;
};

const DefaultLandingRoute = () => {
  const { user, loading } = useAdmin();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={getDefaultRoute(user) || '/access-denied'} replace />;
};

const AppRoutes = () => {
  const { user, loading } = useAdmin();

  if (loading) {
    return <LoadingScreen />;
  }

  const defaultRoute = getDefaultRoute(user) || '/access-denied';

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={defaultRoute} replace /> : <Login />} />
      <Route
        path="/forgot-password"
        element={user ? <Navigate to={defaultRoute} replace /> : <ForgotPassword />}
      />
      <Route
        path="/access-denied"
        element={
          <ProtectedRoute>
            <AccessDenied />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute module="dashboard">
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drivers"
        element={
          <ProtectedRoute module="drivers">
            <Drivers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver-detail"
        element={
          <ProtectedRoute module="driverDetails">
            <DriverDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver-schedule"
        element={
          <ProtectedRoute module="driverSchedule">
            <DriverSchedule defaultScope="drivers" hideScopeToggle />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-schedule"
        element={
          <ProtectedRoute module="staffSchedule">
            <StaffSchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-schedule"
        element={
          <ProtectedRoute module="mySchedule">
            <MySchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/warehouse"
        element={
          <ProtectedRoute module="garage">
            <Warehouse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute module="tasks">
            <Tasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicle-inspection"
        element={
          <ProtectedRoute module="inspection">
            <InspectionsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat-support"
        element={
          <ProtectedRoute module="chatSupport">
            <ChatSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/push-notifications"
        element={
          <ProtectedRoute module="createUser">
            <PushNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute module="createUser">
            <CreateUser />
          </ProtectedRoute>
        }
      />
      <Route path="/create-user" element={<Navigate to="/user-management" replace />} />
      <Route path="/" element={<DefaultLandingRoute />} />
      <Route path="*" element={<DefaultLandingRoute />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AdminProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AdminProvider>
    </ThemeProvider>
  );
}

export default App;
